import { AuthorizedApiBase } from "./authorized-api-base";
import { ApiException, ProblemDetails } from "./taxportal-client";

// ─── Request / Response DTOs ───────────────────────────────────────────────

export interface ImanageToHighqFileItem {
  sourceDocumentId?: string | undefined;
  sourceContainerId?: string | undefined;
  targetParentFolderId?: string | undefined;
  requestId?: number | undefined;
  downloaded?: boolean | undefined;
  fileName?: string;
  overWrite?: boolean | undefined;
  newFileName?: string | undefined;
  isDuplicated?: boolean | undefined;
  documentId?: number | undefined;
  nameWithoutExtension?: string;
}

export interface ImanageToHighqRequest {
  data?: ImanageToHighqFileItem[] | undefined;
  siteId?: number;
  userEmail?: string | undefined;
}

export interface ImanageToHighqResponse {
  success?: boolean;
  message?: string;
  filesForInspection?: ImanageToHighqFileItem[] | undefined;
}
export interface ImanageToHighqConflictWrapper {
  statusCode?: number;
  message?: string;
  data?: ImanageToHighqResponse;
  error?: ImanageToHighqError;
}

export interface ImanageToHighqError {
  isError?: boolean;
  exceptionMessage?: string;
  errorId?: string;
}
export interface HighqToImanageFileItem {
  siteId?: number;
  mdmClientId?: number;
  hQFileId?: string;
  fileName?: string;
  workspaceName?: string;
  hQItemId?: string;
  hQEntityID?: number;
  hQParentFolderId?: string;
  hQParentFolderPath?: string;
  hQEntityName?: string;
  destinationFolderPath?: string;
  isEdited?: boolean;
  isOmitted?: boolean;
}

export interface HighqToImanageRequest {
  data?: HighqToImanageFileItem[] | undefined;
  siteId?: number;
  userEmail?: string | undefined;
}

export interface FileManagementRequest {
  siteId?: number;
  userEmail?: string | undefined;
  clientId?: number | undefined;
  appName?: string | undefined;
  isheetId?: number | undefined;
  itemId?: number | undefined;
  trackId?: number | undefined;
  correlationId?: string | undefined;
  isUpdateAndMoveFilesProcess?: boolean | undefined;
}

export interface FileManagementProcessRunRequest {
  processType: FileManagmentType;
  requestDetails: FileManagementRequest[];
}

export interface HqFileResponse {
  success?: boolean;
  message?: string | undefined;
}

export interface FileManagementSitesResponse {
  offset?: number;
  limit?: number;
  totalCount?: number;
  data?: SiteInfo[];
}

interface ApiResponse<T> {
  statusCode?: number;
  message?: string;
  data?: T;
}

export interface SiteInfo {
  siteId?: number;
  masterClientId?: number;
  clientId?: number;
  siteName?: string;
  siteCreatedBy?: string;
  siteCreatedDateTime?: string;
  status?: string;
  lastUpdatedBy?: string;
  lastUpdatedDateTime?: string;
  message?: string;
}

export enum FileManagmentType {
  UPDATE_FILES_METADATA = 109,
  UPDATE_FILES_LOCATION = 110,
  DOWNLOAD_FILES = 112,
  UPLOAD_FILES = 114,
  UPDATE_IR_METADATA = 115,
  SEND_FILES_TO_DUO = 121,
  UPDATE_AND_MOVE_FILES = 119,
  DOWNLOAD_IR_ATTACHMENT = 184,
  UPLOAD_IR_ATTACHMENT = 185,
}
// ─── Client ────────────────────────────────────────────────────────────────

export class FileManagementServiceClient extends AuthorizedApiBase {
  private http: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> };
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

  constructor(
    baseUrl?: string,
    http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }
  ) {
    super();
    this.http = http ? http : (window as any);
    this.baseUrl = baseUrl ?? "";
  }

  getFileManagementSites(
    type: FileManagmentType,
    userName?: string,
    appName?: string,
    offset: number = 0,
    limit: number = 20,
    searchTerm?: string,
    signal?: AbortSignal
  ): Promise<FileManagementSitesResponse> {
    let url_ = this.baseUrl + "/api/v1/FileManagementTracks/sites?";
    url_ += "type=" + encodeURIComponent(String(type)) + "&";
    if (userName !== undefined) url_ += "userName=" + encodeURIComponent(userName) + "&";
    if (appName !== undefined) url_ += "appName=" + encodeURIComponent(appName) + "&";
    url_ += "offset=" + encodeURIComponent(String(offset)) + "&";
    url_ += "limit=" + encodeURIComponent(String(limit)) + "&";
    if (searchTerm !== undefined) url_ += "searchTerm=" + encodeURIComponent(searchTerm);

    const options_: RequestInit = {
      method: "GET",
      signal,
      headers: { Accept: "application/json" },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => this.http.fetch(url_, transformedOptions_))
      .then((_response: Response) => this.processGetFileManagementSites(_response));
  }

  protected processGetFileManagementSites(response: Response): Promise<FileManagementSitesResponse> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((value: any, key: any) => (_headers[key] = value));
    }

    if (status === 200) {
      return response.text().then((_responseText) =>
        _responseText === ""
          ? ({ totalCount: 0, data: [] } as FileManagementSitesResponse)
          : (JSON.parse(_responseText, this.jsonParseReviver) as ApiResponse<FileManagementSitesResponse>).data ??
            ({ totalCount: 0, data: [] } as FileManagementSitesResponse)
      );
    }
    if (status === 400) {
      return response.text().then((_responseText) => {
        const result = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
        return throwFileManagementException("Bad request.", status, _responseText, _headers, result);
      });
    }
    if (status !== 204) {
      return response.text().then((_responseText) =>
        throwFileManagementException("An unexpected server error occurred.", status, _responseText, _headers)
      );
    }

    return Promise.resolve({ totalCount: 0, data: [] });
  }

  /**
   * Copy files from iManage to HighQ (new microservice endpoint)
   * POST /api/v1/FileTransfer/ImanageToHighq
   */
  imanageToHighq(
    body: ImanageToHighqRequest,
    signal?: AbortSignal
  ): Promise<ImanageToHighqResponse> {
    if (!body || !body.data || body.data.length === 0) {
      return Promise.reject(new Error("Data is invalid"));
    }
    if (body.siteId !== undefined && body.siteId < 0) {
      return Promise.reject(new Error("SiteId is invalid"));
    }

    const url_ = this.baseUrl + "/api/v1/CopyFiles/ImanageToHighq";
    const content_ = JSON.stringify(body);

    const options_: RequestInit = {
      body: content_,
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => this.http.fetch(url_, transformedOptions_))
      .then((_response: Response) => this.processImanageToHighq(_response));
  }

  protected processImanageToHighq(response: Response): Promise<ImanageToHighqResponse> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }

    if (status === 200) {
      return response.text().then((_responseText) => {
        const result = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
        return result as ImanageToHighqResponse;
      });
    } else if (status === 409) {
      return response.text().then((_responseText) => {
        const result =
          _responseText === ""
            ? null
            : JSON.parse(_responseText, this.jsonParseReviver);

        return throwFileManagementException(
          result?.message ?? "Conflicts with files.",
          status,
          _responseText,
          _headers,
          result
        );
      });
    } else if (status === 422) {
      return response.text().then((_responseText) => {
        const result = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
        return throwFileManagementException(
          result?.message ?? "Unprocessable entity.",
          status,
          _responseText,
          _headers,
          result
        );
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        const result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as ProblemDetails);
        return throwFileManagementException("Bad request.", status, _responseText, _headers, result400);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwFileManagementException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<ImanageToHighqResponse>(null as any);
  }

  /**
   * Copy files from HighQ to iManage (new microservice endpoint – fire and forget)
   * POST /api/v1/FileTransfer/HighqToImanage
   * Returns a plain string message (e.g. "Request initiated") when accepted.
   */
  highqToImanage(
    body: HighqToImanageRequest,
    signal?: AbortSignal
  ): Promise<string> {
    if (!body || !body.data || body.data.length === 0) {
      return Promise.reject(new Error("Data is invalid"));
    }
    if (body.siteId !== undefined && body.siteId < 0) {
      return Promise.reject(new Error("SiteId is invalid"));
    }

    const url_ = this.baseUrl + "/api/v1/CopyFiles/HighqToImanage";
    const content_ = JSON.stringify(body);

    const options_: RequestInit = {
      body: content_,
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => this.http.fetch(url_, transformedOptions_))
      .then((_response: Response) => this.processHighqToImanage(_response));
  }

  protected processHighqToImanage(response: Response): Promise<string> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((v: any, k: any) => (_headers[k] = v));
    }

    if (status === 200) {
      return response.text().then((_responseText) => {
        if (_responseText === "") return "";
        // Server may return a plain string or a JSON-encoded string
        try {
          const parsed = JSON.parse(_responseText, this.jsonParseReviver);
          return typeof parsed === "string" ? parsed : (_responseText as string);
        } catch {
          return _responseText;
        }
      });
    } else if (status === 400) {
      return response.text().then((_responseText) => {
        const result400 = _responseText === "" ? null : (JSON.parse(_responseText, this.jsonParseReviver) as ProblemDetails);
        return throwFileManagementException("Bad request.", status, _responseText, _headers, result400);
      });
    } else if (status !== 200 && status !== 204) {
      return response.text().then((_responseText) => {
        return throwFileManagementException("An unexpected server error occurred.", status, _responseText, _headers);
      });
    }
    return Promise.resolve<string>("");
  }

  updateFileMetadata(
    body: FileManagementRequest[],
    signal?: AbortSignal
  ): Promise<HqFileResponse[]> {
    return this.runProcess(FileManagmentType.UPDATE_FILES_METADATA, body, signal);
  }

  moveFiles(
    body: FileManagementRequest[],
    signal?: AbortSignal
  ): Promise<HqFileResponse[]> {
    return this.runProcess(FileManagmentType.UPDATE_FILES_LOCATION, body, signal);
  }

  downloadAcceptedFiles(
    body: FileManagementRequest[],
    signal?: AbortSignal
  ): Promise<HqFileResponse[]> {
    return this.runProcess(FileManagmentType.DOWNLOAD_FILES, body, signal);
  }

  uploadIrAttachment(
    body: FileManagementRequest[],
    signal?: AbortSignal
  ): Promise<HqFileResponse[]> {
    return this.runProcess(FileManagmentType.UPLOAD_IR_ATTACHMENT, body, signal);
  }

  uploadFilesToImanage(
    body: FileManagementRequest[],
    signal?: AbortSignal
  ): Promise<HqFileResponse[]> {
    return this.runProcess(FileManagmentType.UPLOAD_FILES, body, signal);
  }

  runProcess(
    processType: FileManagmentType,
    body: FileManagementRequest[],
    signal?: AbortSignal
  ): Promise<HqFileResponse[]> {
    if (!body || body.length === 0) {
      return Promise.reject(new Error("At least one request is required"));
    }

    const request: FileManagementProcessRunRequest = { processType, requestDetails: body };
    const url_ = this.baseUrl + "/api/v1/FileManagement/FileManagementProcessRun";
    const options_: RequestInit = {
      body: JSON.stringify(request),
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };

    return this.transformOptions(options_)
      .then((transformedOptions_) => this.http.fetch(url_, transformedOptions_))
      .then((_response: Response) => this.processHqFileResponse<HqFileResponse[]>(_response));
  }

  protected processHqFileResponse<TResponse>(response: Response): Promise<TResponse> {
    const status = response.status;
    const _headers: any = {};
    if (response.headers && response.headers.forEach) {
      response.headers.forEach((value: any, key: any) => (_headers[key] = value));
    }

    if (status === 200) {
      return response.text().then((_responseText) =>
        (_responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver)) as TResponse
      );
    }
    if (status === 400) {
      return response.text().then((_responseText) => {
        const result = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
        return throwFileManagementException("Bad request.", status, _responseText, _headers, result);
      });
    }
    if (status !== 204) {
      return response.text().then((_responseText) =>
        throwFileManagementException("An unexpected server error occurred.", status, _responseText, _headers)
      );
    }

    return Promise.resolve<TResponse>(null as any);
  }
}

function throwFileManagementException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any
): any {
  if (result !== null && result !== undefined) throw result;
  else throw new ApiException(message, status, response, headers, null);
}
